/**
 * Shared design tokens + style builders for {@link GraphNodeFrame}.
 *
 * These are the mindmap's node-chrome primitives, lifted verbatim so the
 * flowchart (and any future graph widget) can render nodes with the identical
 * visual system: a dark inset fill, depth-tinted inset rings, and a
 * shape-following selection glow.
 *
 * The numeric/string values here are the canonical source; `mindmap-constants`
 * re-exports them so existing mindmap imports keep working unchanged.
 */

import type { CSSProperties } from 'react';

/** Dark inset fill that sits inside every node's accent/border layer. */
export const NODE_FILL = '#131823';

/** Default node label color (85% white). */
export const NODE_TEXT = 'rgba(255, 255, 255, 0.85)';

/** Clip-path for the preparation/hexagon silhouette (mindmap + flowchart). */
export const HEXAGON_CLIP_PATH =
  'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';

/** Opacity applied to a node that is dimmed (not on the active/highlight path). */
export const DIMMED_OPACITY = 0.25;

/** Shared interaction transition duration for hover/select/dim state changes. */
export const TRANSITION_DURATION = '150ms';

/**
 * Root-node radial background (mindmap root / flowchart Start terminator).
 * Exposed so terminators can opt into the same "origin" treatment.
 */
export const ROOT_BACKGROUND =
  'radial-gradient(circle at 30% 30%, rgba(102, 254, 193, 0.3), rgba(0, 55, 29, 0.7) 85%)';

/**
 * Depth tiers for the inset-ring box shadow. The mindmap maps:
 *   root → Root, depth <= 1 → Secondary, deeper → Further.
 * A consumer picks the tier explicitly; {@link getDepthBoxShadow} returns it.
 */
export enum GraphNodeDepthTier {
  Root = 'root',
  Secondary = 'secondary',
  Further = 'further',
}

/** Accent ring used by the Root tier (green inset, driven by the first depth color). */
const ROOT_BOX_SHADOW = `#00000080 1px 2px 6px, inset 0 0 0 2px #66FEC1, inset #ffffff7a 1px 2px 4px`;
const SECONDARY_BOX_SHADOW =
  'inset 0 0 0 1.5px rgba(147, 216, 255, 0.5), #0000008a 1px 2px 5px, inset #ffffff38 1px 2px 6px';
const FURTHER_BOX_SHADOW =
  'inset 0 0 0 1px rgba(147, 216, 255, 0.28), #00000066 1px 1px 3px, inset #ffffff22 1px 1px 4px';

const ROOT_SELECTED_GLOW = '0 0 30px -4px rgba(102, 254, 193, 0.55)';
const SECONDARY_SELECTED_GLOW = '0 0 24px -4px rgba(147, 216, 255, 0.55)';
const FURTHER_SELECTED_GLOW = '0 0 20px -4px rgba(147, 216, 255, 0.4)';

const DEPTH_BOX_SHADOW_BY_TIER: Record<GraphNodeDepthTier, string> = {
  [GraphNodeDepthTier.Root]: ROOT_BOX_SHADOW,
  [GraphNodeDepthTier.Secondary]: SECONDARY_BOX_SHADOW,
  [GraphNodeDepthTier.Further]: FURTHER_BOX_SHADOW,
};

const SELECTED_GLOW_BY_TIER: Record<GraphNodeDepthTier, string> = {
  [GraphNodeDepthTier.Root]: ROOT_SELECTED_GLOW,
  [GraphNodeDepthTier.Secondary]: SECONDARY_SELECTED_GLOW,
  [GraphNodeDepthTier.Further]: FURTHER_SELECTED_GLOW,
};
const BOX_SHADOW_INSET_TOKEN = 'inset';

/** The inset-ring + drop-shadow stack for a depth tier (resting state). */
export function getDepthBoxShadow(tier: GraphNodeDepthTier): string {
  return DEPTH_BOX_SHADOW_BY_TIER[tier];
}

/** The outer selection glow for a depth tier (appended when selected). */
export function getSelectedGlow(tier: GraphNodeDepthTier): string {
  return SELECTED_GLOW_BY_TIER[tier];
}

/**
 * How a node's silhouette is drawn. The selection glow + accent border always
 * follow this silhouette (never a rectangle behind a non-rect shape):
 *
 * - {@link GraphNodeShapeKind.BorderRadius} — a single rounded element (rect,
 *   stadium pill, circle) whose accent/depth comes from `boxShadow` and whose
 *   selection is a real 1px border + glow. This is the mindmap's rounded card.
 * - {@link GraphNodeShapeKind.ClipPath} — a two-layer clip-path silhouette
 *   (diamond, parallelogram, trapezoid, hexagon): an accent/border layer with a
 *   `NODE_FILL` inset on top, clipped to the same path. This is the mindmap hexagon.
 * - {@link GraphNodeShapeKind.SvgBody} — an arbitrary inline-SVG body
 *   (cylinder, document) drawn behind the label; the SVG owns its own
 *   stroke/fill so the border follows the drawn path exactly.
 */
export enum GraphNodeShapeKind {
  BorderRadius = 'borderRadius',
  ClipPath = 'clipPath',
  SvgBody = 'svgBody',
}

/** Rounded silhouette: rect / stadium / circle. */
export interface GraphNodeBorderRadiusShape {
  kind: GraphNodeShapeKind.BorderRadius;
  /** CSS border-radius (number = px, string passed through). */
  borderRadius: number | string;
}

/** Clip-path silhouette: diamond / parallelogram / trapezoid / hexagon. */
export interface GraphNodeClipPathShape {
  kind: GraphNodeShapeKind.ClipPath;
  /** CSS clip-path applied to both the accent layer and the inset fill layer. */
  clipPath: string;
}

/**
 * Arbitrary inline-SVG silhouette: cylinder / document / predefined.
 * The render callback receives the node's pixel size and the resolved accent +
 * fill, and must return an `<svg>` (or `<g>`) drawing the body. The accent
 * border follows whatever path the SVG strokes.
 */
export interface GraphNodeSvgBodyShape {
  kind: GraphNodeShapeKind.SvgBody;
  renderBody: (args: GraphNodeSvgBodyArgs) => React.ReactNode;
}

/** Arguments handed to {@link GraphNodeSvgBodyShape.renderBody}. */
export interface GraphNodeSvgBodyArgs {
  width: number;
  height: number;
  /** Resolved accent (the stroke the body should use). */
  accentColor: string;
  /** Resolved fill (the body interior). */
  fill: string;
  /** Whether the node is selected (so the body can swap to the white silhouette). */
  isSelected: boolean;
}

/** Discriminated union of every silhouette {@link GraphNodeFrame} can draw. */
export type GraphNodeShapeDescriptor =
  | GraphNodeBorderRadiusShape
  | GraphNodeClipPathShape
  | GraphNodeSvgBodyShape;

/**
 * Convert an OUTER `box-shadow` glow list into an equivalent
 * `filter: drop-shadow(...)` value, so a non-rectangular silhouette (clip-path /
 * svg body) casts a halo that traces its actual alpha edge instead of the
 * wrapper's bounding box.
 *
 * `drop-shadow()` has no spread radius and no `inset`, so each `box-shadow`
 * layer is reduced to `offsetX offsetY blur color` (a negative spread — used by
 * the resting selection glows to pull the halo tight — is dropped, and the blur
 * is trimmed to compensate so the halo keeps a comparable footprint). Any
 * `inset` layer is skipped: inset rings cannot be a drop-shadow and the
 * clip/svg silhouettes never pass one here (their inset accent is drawn on the
 * silhouette itself). Returns `undefined` when there is nothing to cast.
 *
 * Splitting is parenthesis-aware so `rgba(…, …)` / `color-mix(…, …)` colors
 * (which contain commas and spaces) survive intact.
 */
export function boxShadowGlowToDropShadowFilter(boxShadow: string | undefined): string | undefined {
  if (!boxShadow) {
    return undefined;
  }

  const layers = splitTopLevel(boxShadow, ',');
  const drops: string[] = [];
  for (const layer of layers) {
    const drop = layerToDropShadow(layer);
    if (drop !== undefined) {
      drops.push(drop);
    }
  }

  return drops.length > 0 ? drops.join(' ') : undefined;
}

/** A single `box-shadow` layer → one `drop-shadow(...)`, or undefined to skip. */
function layerToDropShadow(layer: string): string | undefined {
  const tokens = splitTopLevel(layer.trim(), ' ').filter((token) => token.length > 0);
  if (tokens.length === 0 || tokens.some((token) => token === BOX_SHADOW_INSET_TOKEN)) {
    return undefined;
  }

  // Leading length tokens are offsetX, offsetY, blur, [spread]; the remainder is
  // the color (kept joined so multi-word color functions stay whole).
  const lengths: string[] = [];
  let colorStart = tokens.length;
  for (let i = 0; i < tokens.length; i += 1) {
    if (isLengthToken(tokens[i])) {
      lengths.push(tokens[i]);
      continue;
    }
    colorStart = i;
    break;
  }

  const color = tokens.slice(colorStart).join(' ');
  if (color.length === 0 || lengths.length < 2) {
    return undefined;
  }

  const offsetX = lengths[0];
  const offsetY = lengths[1];
  const blur = trimBlurForDropShadow(lengths[2]);
  return `drop-shadow(${offsetX} ${offsetY} ${blur} ${color})`;
}

/** True for a CSS length/number token (the geometry part of a shadow layer). */
function isLengthToken(token: string): boolean {
  return /^-?(\d+\.?\d*|\.\d+)(px|em|rem|%)?$/.test(token);
}

/**
 * Box-shadow blur reads larger than the same drop-shadow blur (box-shadow also
 * spreads the opaque core), and the resting glows used a negative spread to keep
 * the halo tight. Trimming the blur to ~60% reproduces a comparable footprint
 * without the (unsupported) spread. Missing blur → 0.
 */
function trimBlurForDropShadow(blur: string | undefined): string {
  if (blur === undefined) {
    return '0';
  }

  const match = /^(-?(?:\d+\.?\d*|\.\d+))(px|em|rem|%)?$/.exec(blur);
  if (!match) {
    return blur;
  }

  const value = Math.max(0, Math.round(Number(match[1]) * 0.6 * 100) / 100);
  const unit = match[2] ?? '';
  return `${value}${unit}`;
}

/** Split on `delimiter`, but only at the top level (ignoring `(...)` nesting). */
function splitTopLevel(value: string, delimiter: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of value) {
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    }

    if (char === delimiter && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  parts.push(current);
  return parts;
}

/** Selection silhouette color — the white the mindmap uses for a selected border. */
export const SELECTED_SILHOUETTE_COLOR = '#ffffffad';

/** Padding fallbacks shared by the frame's content layer. */
export const NODE_CONTENT_PADDING_DEFAULT: CSSProperties['padding'] = '0 14px';
